import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Heading,
  SectionHeader,
  Switch,
  Tabs,
  TrevorIcon,
  TTab,
} from '@trevor/ui-components';
import { useSelector } from 'react-redux';
import { getDashboardsState } from '@/store-selectors';
import { SharedWithOthersTab } from '@/features/dashboard/components/SharePublishEmbedSidebar/components/SharedWithOthersTab';
import { EmbedDashboardTab } from '@/features/dashboard/components/SharePublishEmbedSidebar/components/EmbedDashboardTab';
import { useUpdatePublicPermissionMutation } from '@/features/dashboard/apiSlice';
import { useDatasourceId } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  toggleSharePublishEmbedSidebar,
  selectIsSharePublishEmbedSidebarOpen,
} from '@/features/dashboard/dashboardSlice';
import ResizeableSidebar from '@/components/ResizeableSidebar';
import { loadDashboard, modalInteraction } from '@/actions';
import { DASHBOARDS_VIEW } from '@/util/enums/EnumModals';
import { userAction } from '@/util/helpers/ajax-helpers';

const ResizeableSidebarContainer = styled.div`
  padding: 22px var(--spacing-l);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  width: 100%;
`;

const CloseButton = styled(Button).attrs({
  variant: 'secondary',
  size: 'small',
  icon: <TrevorIcon name="close" />,
})`
  position: absolute;
  top: 22px;
  right: var(--spacing-l);
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const tabs: TTab[] = [
  { iconName: 'invitation-envelop', label: 'Share with others' },
  {
    iconName: 'sql',
    label: 'Embed dashboard',
  },
];

export const SharePublishEmbedSidebar = () => {
  const dispatch = useAppDispatch();
  const dashboardState = useSelector(getDashboardsState);
  const datasourceId = useDatasourceId();
  const [updatePublicPermission, { isLoading: updatePublicPermissionLoading }] =
    useUpdatePublicPermissionMutation();
  const isVisible = useAppSelector(selectIsSharePublishEmbedSidebarOpen);

  const dashboardId = dashboardState.active?.id();
  const isPublic = dashboardState.public;

  const [activeTab, setActiveTab] = React.useState(0);

  const handleChangeActiveBar = (value: number) => {
    userAction('embed-dashboards-sidebar', {
      action: 'tab-change',
      value: value === 0 ? 'share-with-others' : 'embed-dashboard',
    });
    setActiveTab(value);
  };

  const handleTogglePublicPermission = async () => {
    await updatePublicPermission({
      datasourceId,
      dashboardId,
      body: {
        isPublic: !isPublic,
      },
    });
    dispatch(modalInteraction(DASHBOARDS_VIEW, { section: 'toggle-public' }));

    if (!isPublic) {
      dispatch(
        modalInteraction(DASHBOARDS_VIEW, {
          section: 'applied-filters',
          filters: [],
        }),
      );
      dispatch(loadDashboard(dashboardState.active));
    }
    userAction(`make-dashboard-${isPublic ? 'private' : 'public'}`, {
      dashboard_id: dashboardId,
    });
  };

  const handleCloseSidebar = () => {
    dispatch(toggleSharePublishEmbedSidebar());
  };

  return (
    <ResizeableSidebar initialWidth={464} isVisible={isVisible} isRightSidebar>
      <ResizeableSidebarContainer>
        <CloseButton onClick={handleCloseSidebar} />
        <Heading variant={4}>Share, publish or embed dashboard</Heading>
        <Container>
          <SectionHeader
            iconName={isPublic ? 'eye' : 'eye-closed'}
            title="Privacy setting"
            subtitle={
              isPublic
                ? 'Anyone with the link can view'
                : 'Only accessible to members of your datasource'
            }
          />
          <Switch
            loading={updatePublicPermissionLoading}
            onChange={handleTogglePublicPermission}
            checkedLabel="Public"
            uncheckedLabel="Private"
            checked={isPublic}
          />
        </Container>
        <Tabs
          activeTab={activeTab}
          tabs={tabs}
          contents={[
            <SharedWithOthersTab key="share-with-others" />,
            <EmbedDashboardTab key="embed-dashboard" />,
          ]}
          onChange={handleChangeActiveBar}
        />
      </ResizeableSidebarContainer>
    </ResizeableSidebar>
  );
};
